if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      connectedCallback() {
        this.summary = this.querySelector('[data-bb-summary]');
        if (!this.summary) return;

        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
        this.root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        this.items = [];
        this.notice = '';
        // "mrp" while a store-wide sale is on: prices are shown off the MRP
        // (compare-at price) and the box discount is the total % off MRP.
        this.basis = this.dataset.basis || 'price';
        this.basePercent = Number(this.dataset.basePercent) || 0;

        this.tierInputs = Array.from(this.querySelectorAll('.bb__tier input'));
        this.slotTemplate = this.querySelector('[data-bb-slot-template]');

        this.cards = Array.from(this.querySelectorAll('[data-bb-card]')).map((el) => {
          const data = JSON.parse(el.querySelector('[data-bb-product]').textContent);
          const checked = el.querySelector('.bb-card__variants input:checked');
          const variant =
            (checked && data.variants.find((v) => v.id === Number(checked.value))) ||
            data.variants.find((v) => v.available) ||
            data.variants[0];
          return { el, data, variant };
        });

        this.tierInputs.forEach((input) => input.addEventListener('change', () => this.setTier(true)));

        this.cards.forEach((card) => {
          card.el.querySelector('[data-bb-add]')?.addEventListener('click', () => this.add(card));
          card.el.querySelector('[data-bb-plus]')?.addEventListener('click', () => this.add(card));
          card.el.querySelector('[data-bb-minus]')?.addEventListener('click', () => this.removeOne(card));
          card.el.querySelectorAll('.bb-card__variants input').forEach((input) =>
            input.addEventListener('change', () => this.selectVariant(card, Number(input.value)))
          );
        });

        this.querySelector('[data-bb-slots]').addEventListener('click', (event) => {
          const button = event.target.closest('[data-bb-remove]');
          if (!button) return;
          this.items.splice(Number(button.dataset.index), 1);
          this.notice = '';
          this.render();
        });

        this.querySelector('[data-bb-toggle]').addEventListener('click', () => this.toggleSummary());
        this.querySelector('[data-bb-submit]').addEventListener('click', () => this.submit());
        this.querySelector('[data-bb-replace]').addEventListener('click', () => this.submit(true));
        this.querySelector('[data-bb-keep]').addEventListener('click', () => this.hideConflict());

        this.setTier(false);
      }

      /* ---------- Helpers ---------- */
      formatMoney(cents) {
        const value = (Number(cents) || 0) / 100;
        const withDelimiters = (number, decimals, thousands = ',', decimal = '.') => {
          const [whole, fraction] = number.toFixed(decimals).split('.');
          const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return fraction ? grouped + decimal + fraction : grouped;
        };
        return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          switch (key) {
            case 'amount_no_decimals':
              return withDelimiters(value, 0);
            case 'amount_with_comma_separator':
              return withDelimiters(value, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return withDelimiters(value, 0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return withDelimiters(value, 2, "'", '.');
            default:
              return withDelimiters(value, 2);
          }
        });
      }

      discounted(cents) {
        return Math.round(cents * (1 - this.tier.discount / 100));
      }

      mrpOf(variant) {
        return this.basis === 'mrp' && variant.mrp ? variant.mrp : variant.price;
      }

      // Price per unit once the box is full. In MRP mode the sale price is
      // already basePercent off MRP, so only the rest of the tier is taken off
      // it (25% tier on a 20% sale price = 6.25% more), matching the cart.
      boxPrice(variant) {
        if (this.basis !== 'mrp') return this.discounted(variant.price);
        const factor = (100 - this.tier.discount) / (100 - this.basePercent);
        return Math.round(variant.price * factor);
      }

      fillText(text, values) {
        return Object.keys(values).reduce((out, key) => out.split(`[${key}]`).join(values[key]), text || '');
      }

      /* ---------- Actions ---------- */
      setTier(userChange) {
        const input = this.tierInputs.find((i) => i.checked) || this.tierInputs[0];
        this.tier = {
          size: Number(input.dataset.size) || 1,
          discount: Number(input.dataset.discount) || 0,
          code: input.dataset.code || '',
          label: input.dataset.label || '',
        };

        this.notice = '';
        if (this.items.length > this.tier.size) {
          this.items = this.items.slice(0, this.tier.size);
          if (userChange) this.notice = this.dataset.trimText;
        }
        this.render();
      }

      add(card) {
        if (!card.variant || !card.variant.available) return;
        if (this.items.length >= this.tier.size) {
          this.summary.classList.remove('is-bump');
          void this.summary.offsetWidth;
          this.summary.classList.add('is-bump');
          return;
        }
        this.items.push({ product: card.data, variant: card.variant });
        this.notice = '';
        this.render();
      }

      removeOne(card) {
        // Prefer removing the size that is currently selected on the card
        let index = -1;
        for (let i = this.items.length - 1; i >= 0; i--) {
          if (this.items[i].variant.id === card.variant.id) {
            index = i;
            break;
          }
        }
        if (index === -1) {
          for (let i = this.items.length - 1; i >= 0; i--) {
            if (this.items[i].product.id === card.data.id) {
              index = i;
              break;
            }
          }
        }
        if (index === -1) return;
        this.items.splice(index, 1);
        this.notice = '';
        this.render();
      }

      selectVariant(card, variantId) {
        const variant = card.data.variants.find((v) => v.id === variantId);
        if (!variant) return;
        card.variant = variant;

        const img = card.el.querySelector('.bb-card__media img');
        if (img && variant.card_image) {
          img.removeAttribute('srcset');
          img.src = variant.card_image;
        }
        this.render();
      }

      toggleSummary(force) {
        const open = typeof force === 'boolean' ? force : !this.summary.classList.contains('is-open');
        this.summary.classList.toggle('is-open', open);
        this.querySelector('[data-bb-toggle]').setAttribute('aria-expanded', String(open));
      }

      /* ---------- Render ---------- */
      render() {
        const { size, discount } = this.tier;
        const count = this.items.length;
        const full = count === size;
        const mrpMode = this.basis === 'mrp';
        const subtotal = this.items.reduce((sum, item) => sum + this.mrpOf(item.variant), 0);
        let saving = subtotal - this.discounted(subtotal);
        let shownPercent = discount;
        if (mrpMode) {
          const payNow = full
            ? this.items.reduce((sum, item) => sum + this.boxPrice(item.variant), 0)
            : this.items.reduce((sum, item) => sum + item.variant.price, 0);
          saving = subtotal - payNow;
          shownPercent = full ? discount : this.basePercent;
        }

        this.classList.toggle('is-full', full);
        this.querySelectorAll('[data-bb-status]').forEach((el) => (el.textContent = `${count}/${size}`));
        this.querySelector('[data-bb-progress]').style.width = `${(count / size) * 100}%`;

        const message = this.querySelector('[data-bb-message]');
        if (this.notice) {
          message.textContent = this.notice;
        } else if (full) {
          message.textContent = this.fillText(this.dataset.fullText, { discount });
        } else {
          message.textContent = this.fillText(this.dataset.moreText, { count: size - count, discount });
        }
        message.classList.toggle('is-success', full && !this.notice);

        // Slots
        const slots = this.querySelector('[data-bb-slots]');
        slots.innerHTML = '';
        for (let i = 0; i < size; i++) {
          const item = this.items[i];
          if (item) {
            const li = this.slotTemplate.content.firstElementChild.cloneNode(true);
            const img = li.querySelector('img');
            const src = item.variant.image || item.product.image;
            if (src) {
              img.src = src;
            } else {
              img.remove();
            }
            li.querySelector('.bb-slot__title').textContent = item.product.title;
            const variantText = item.variant.title === 'Default Title' ? '' : item.variant.title;
            li.querySelector('.bb-slot__variant').textContent = variantText;
            li.querySelector('.bb-slot__price').textContent = this.formatMoney(item.variant.price);
            const remove = li.querySelector('[data-bb-remove]');
            remove.dataset.index = i;
            remove.querySelector('.visually-hidden').textContent += `: ${item.product.title}`;
            slots.appendChild(li);
          } else {
            const li = document.createElement('li');
            li.className = 'bb-slot is-empty';
            li.innerHTML = `<span class="bb-slot__thumb"><span>${i + 1}</span></span>`;
            slots.appendChild(li);
          }
        }

        // Totals
        this.querySelector('[data-bb-subtotal]').textContent = this.formatMoney(subtotal);
        this.querySelector('[data-bb-percent]').textContent = shownPercent ? `(${shownPercent}%)` : '';
        const savingNow = full || mrpMode ? saving : 0;
        this.querySelector('[data-bb-saving]').textContent = `-${this.formatMoney(savingNow)}`;
        this.querySelector('[data-bb-total]').textContent = this.formatMoney(subtotal - savingNow);
        this.summary.classList.toggle('has-saving', savingNow > 0);

        // Cards
        this.cards.forEach((card) => {
          const productCount = this.items.filter((item) => item.product.id === card.data.id).length;
          const variantCount = this.items.filter((item) => item.variant.id === card.variant.id).length;
          const badge = card.el.querySelector('[data-bb-count]');
          badge.hidden = productCount === 0;
          badge.textContent = `×${productCount}`;
          card.el.classList.toggle('is-selected', productCount > 0);

          const addButton = card.el.querySelector('[data-bb-add]');
          const stepper = card.el.querySelector('[data-bb-stepper]');
          if (addButton && stepper) {
            addButton.hidden = variantCount > 0;
            stepper.hidden = variantCount === 0;
            stepper.querySelector('[data-bb-qty]').textContent = variantCount;
            addButton.disabled = full || !card.variant.available;
            stepper.querySelector('[data-bb-plus]').disabled = full;
          }

          card.el.querySelector('[data-bb-compare]').textContent = this.formatMoney(this.mrpOf(card.variant));
          card.el.querySelector('[data-bb-compare]').hidden = !discount;
          card.el.querySelector('[data-bb-price]').textContent = this.formatMoney(this.boxPrice(card.variant));
        });

        const submit = this.querySelector('[data-bb-submit]');
        submit.disabled = !full;
        this.querySelector('[data-bb-error]').hidden = true;
        this.hideConflict();
      }

      /* ---------- Add to cart ---------- */
      async submit(replace = false) {
        if (this.items.length !== this.tier.size || this.busy) return;
        this.busy = true;

        const submit = this.querySelector('[data-bb-submit]');
        const spinner = this.querySelector('[data-bb-spinner]');
        const error = this.querySelector('[data-bb-error]');
        submit.setAttribute('aria-disabled', 'true');
        submit.classList.add('loading');
        spinner.classList.remove('hidden');
        error.hidden = true;
        this.hideConflict();

        // Only one box size per order: a discount code covers the whole cart,
        // so a second size would change the first box's discount too.
        // Without a code (store-wide sale) any mix of boxes is fine.
        if (this.tier.code) try {
          const cart = await (await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } })).json();
          const otherBoxLines = (cart.items || []).filter(
            (line) => line.properties && line.properties._bundle && line.properties._bundle !== this.tier.label
          );

          if (otherBoxLines.length) {
            if (!replace) {
              this.showConflict(otherBoxLines[0].properties._bundle);
              this.reset(submit, spinner);
              return;
            }
            const updates = {};
            otherBoxLines.forEach((line) => (updates[line.key] = 0));
            await fetch(`${this.root}cart/update.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates }),
            });
          }
        } catch (e) {
          /* if the cart can't be read, add the box as usual */
        }

        const bundleId = Date.now().toString(36);
        const grouped = new Map();
        this.items.forEach((item) => grouped.set(item.variant.id, (grouped.get(item.variant.id) || 0) + 1));
        const items = Array.from(grouped, ([id, quantity]) => ({
          id,
          quantity,
          properties: { _bundle: this.tier.label, _bundle_id: bundleId },
        }));

        try {
          const response = await fetch(`${this.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || 'Error');

          if (this.tier.code) await this.applyDiscount(this.tier.code);

          const drawer = document.querySelector('cart-drawer');
          if (drawer && this.dataset.redirect !== '/checkout') {
            await this.openDrawer(drawer);
            this.items = [];
            this.render();
            this.reset(submit, spinner);
            return;
          }

          window.location.href = `${this.root}${(this.dataset.redirect || '/cart').replace(/^\//, '')}`;
        } catch (e) {
          error.textContent = e.message;
          error.hidden = false;
          this.reset(submit, spinner);
        }
      }

      showConflict(oldLabel) {
        const box = this.querySelector('[data-bb-conflict]');
        const values = { old: oldLabel, new: this.tier.label };
        box.querySelector('[data-bb-conflict-text]').textContent = this.fillText(box.dataset.text, values);
        box.querySelector('[data-bb-replace]').textContent = this.fillText(box.dataset.replace, values);
        box.querySelector('[data-bb-keep]').textContent = this.fillText(box.dataset.keep, values);
        box.hidden = false;
      }

      hideConflict() {
        const box = this.querySelector('[data-bb-conflict]');
        if (box) box.hidden = true;
      }

      reset(submit, spinner) {
        submit.removeAttribute('aria-disabled');
        submit.classList.remove('loading');
        spinner.classList.add('hidden');
        this.busy = false;
      }

      // Puts the bundle code on the cart (shown in the drawer) and in the
      // discount cookie that checkout reads
      async applyDiscount(code) {
        try {
          await fetch(`${this.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: code }),
          });
        } catch (e) {
          /* the link below still sets it */
        }
        try {
          await fetch(`${this.root}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
        } catch (e) {
          /* the code can still be entered at checkout */
        }
      }

      // Refreshes the theme's cart drawer and slides it open
      async openDrawer(drawer) {
        const ids = drawer.getSectionsToRender().map((section) => section.id);
        const response = await fetch(`${this.root}?sections=${ids.join(',')}`);
        const sections = await response.json();
        // An empty cart drawer hides its header and items until this class goes
        drawer.classList.remove('is-empty');
        drawer.renderContents({ id: null, sections });
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder' });
        }
      }
    }
  );
}